<template>
  <teleport to="body">
    <div id="toastbar" :class="[toastType, durationType, { show: isRevealed }]">
      <div class="toastcontent" v-html="toastContent"></div>
    </div>
  </teleport>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { Events, useEventBus } from '@/helpers/event-bus'

const { t } = useI18n()
const eventBus = useEventBus()
const isRevealed = ref(false)
const toastContent = ref('')
const toastType = ref('')
const durationType = ref('')

const durations: { [key: string]: number } = {
  short: 2500,
  default: 4000,
  long: 6000,
}

onMounted(() => {
  eventBus.on(Events.SHOW_SUCCESS, onShowSuccess)
  eventBus.on(Events.SHOW_INFO, onShowInfo)
  eventBus.on(Events.SHOW_ERROR, onShowError)
  eventBus.on(Events.SHOW_SYSTEM_ERROR, onShowSystemError)
  eventBus.on(Events.DEFAULT_ERROR, onShowDefaultError)
})

const showToast = function (content: string, type: string, duration: string) {
  isRevealed.value = true
  toastType.value = type
  toastContent.value = content
  setTimeout(() => {
    hideToast()
  }, durations[duration])
}
const hideToast = function () {
  isRevealed.value = false
  toastType.value = ''
  toastContent.value = ''
}

const onShowSuccess = function (data: { key: string }): void {
  showToast(t(data.key), 'success', 'short')
}

const onShowInfo = function (data: { key: string }): void {
  showToast(t(data.key), 'default', 'short')
}

const onShowError = function (data: { key: string }): void {
  showToast(t(data.key), 'error', 'default')
}

const onShowSystemError = function (data: { err: string; key: string }): void {
  const title = data.key ? t(data.key) : t('shared.default_error')
  const message = title + '<br><small><i>' + data.err + '</i></small>'
  showToast(message, 'error', 'long')
}

const onShowDefaultError = function (): void {
  showToast(t('shared.default_error'), 'error', 'default')
}
</script>

<style lang="postcss" scoped>
#toastbar {
  @apply fixed w-full z-90 bottom-0 pointer-events-none hidden;

  .toastcontent {
    @apply px-8 py-3 text-white text-center rounded-md bg-neutral-950;
    min-width: 250px;
    max-width: 500px;
  }
  &.show {
    @apply flex justify-center;
    bottom: 4rem;
    -webkit-animation:
      fadein 0.5s,
      fadeout 0.5s 4s;
    animation:
      fadein 0.5s,
      fadeout 0.5s 4s;
    &.short {
      -webkit-animation:
        fadein 0.5s,
        fadeout 0.5s 2.5s;
      animation:
        fadein 0.5s,
        fadeout 0.5s 2.5s;
    }
    &.long {
      -webkit-animation:
        fadein 0.5s,
        fadeout 0.5s 6s;
      animation:
        fadein 0.5s,
        fadeout 0.5s 6s;
    }
  }
  &.error .toastcontent {
    @apply bg-error-default;
  }
  &.success .toastcontent {
    @apply bg-success-default;
  }
}

@-webkit-keyframes fadein {
  from {
    bottom: 0;
    opacity: 0;
  }
  to {
    bottom: 4rem;
    opacity: 1;
  }
}

@keyframes fadein {
  from {
    bottom: 0;
    opacity: 0;
  }
  to {
    bottom: 4rem;
    opacity: 1;
  }
}

@-webkit-keyframes fadeout {
  from {
    bottom: 4rem;
    opacity: 1;
  }
  to {
    bottom: 0;
    opacity: 0;
  }
}

@keyframes fadeout {
  from {
    bottom: 4rem;
    opacity: 1;
  }
  to {
    bottom: 0;
    opacity: 0;
  }
}
</style>
